import { getBlogPost, updateBlogPost } from '../../util/api';
import { storableError } from '../../util/errors';
import { denormalisedResponseEntities } from '../../util/data';
import { createImageVariantConfig } from '../../util/sdkLoader';

// Action types
export const FETCH_BLOG_REQUEST = 'app/BlogContentPage/FETCH_BLOG_REQUEST';
export const FETCH_BLOG_SUCCESS = 'app/BlogContentPage/FETCH_BLOG_SUCCESS';
export const FETCH_BLOG_ERROR = 'app/BlogContentPage/FETCH_BLOG_ERROR';

export const SHOW_LISTING_REQUEST = 'app/BlogContentPage/SHOW_LISTING_REQUEST';
export const SHOW_LISTING_SUCCESS = 'app/BlogContentPage/SHOW_LISTING_SUCCESS';
export const SHOW_LISTING_ERROR = 'app/BlogContentPage/SHOW_LISTING_ERROR';

// Action creators
export const fetchBlogRequest = id => ({ type: FETCH_BLOG_REQUEST, payload: { id } });
export const fetchBlogSuccess = blog => ({ type: FETCH_BLOG_SUCCESS, payload: { blog } });
export const fetchBlogError = error => ({ type: FETCH_BLOG_ERROR, payload: error, error: true });

export const showListingRequest = id => ({ type: SHOW_LISTING_REQUEST, payload: { id } });
export const showListingSuccess = listing => ({ type: SHOW_LISTING_SUCCESS, payload: { listing } });
export const showListingError = error => ({ type: SHOW_LISTING_ERROR, payload: error, error: true });

// Initial state
const initialState = {
  blog: null,
  blogLoading: false,
  blogError: null,
  listing: null,
  listingLoading: false,
  listingError: null,
};

// Reducer
const blogContentPageReducer = (state = initialState, action) => {
  const { type, payload } = action;
  switch (type) {
    case FETCH_BLOG_REQUEST:
      return { ...state, blogLoading: true, blogError: null };
    case FETCH_BLOG_SUCCESS:
      return { ...state, blogLoading: false, blog: payload.blog };
    case FETCH_BLOG_ERROR:
      return { ...state, blogLoading: false, blogError: payload };
    case SHOW_LISTING_REQUEST:
      return { ...state, listingLoading: true, listingError: null };
    case SHOW_LISTING_SUCCESS:
      return { ...state, listingLoading: false, listing: payload.listing };
    case SHOW_LISTING_ERROR:
      return { ...state, listingLoading: false, listingError: payload };
    default:
      return state;
  }
};
export default blogContentPageReducer;

// Thunk: fetch a blog by blogId
export const requestFetchBlog = blogId => async dispatch => {
  dispatch(fetchBlogRequest(blogId));
  try {
    const response = await getBlogPost(blogId);
    // Flatten blog data for UI
    const blog = response.data;
    
    dispatch(fetchBlogSuccess(blog));
    return blog;
  } catch (e) {
    dispatch(fetchBlogError(storableError(e)))
  }
};

// Thunk: increment blog views and clicks

export const incrementBlogViewsAndClicks = (blogId, incrementClicks = false) => async (dispatch, getState) => {
  try {
    const { blog } = getState().BlogContentPage || {};
    if(!blog) return;
    const newViews = (blog.views || 0) + 1;
    const newClicks = incrementClicks ? (blog.clicks || 0) + 1 : blog.clicks;
    await updateBlogPost(blogId, { views: newViews, clicks: newClicks });
    // Optionally, you can re-fetch the blog or optimistically update state here
  } catch (e) {
    // Optionally handle error
    // console.error('Failed to increment blog views/clicks', e);
  }
};

// Thunk: fetch a listing by listingId (mirrors showListing)
export const requestShowListing = (listingId, config, isOwn = false) => (dispatch, getState, sdk) => {
  dispatch(showListingRequest(listingId));
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  const aspectRatio = aspectHeight / aspectWidth;

  dispatch(showListingRequest(listingId));

  const params = {
    id: listingId,
    include: ['author', 'author.profileImage', 'images', 'currentStock'],
    'fields.image': [
      // Scaled variants for large images
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.scaled-xlarge',

      // Cropped variants for listing thumbnail images
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      `variants.${variantPrefix}-4x`,
      `variants.${variantPrefix}-6x`,

      // Social media
      'variants.facebook',
      'variants.twitter',

      // Avatars
      'variants.square-small',
      'variants.square-small2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
  };

  const show = isOwn ? sdk.ownListings.show(params) : sdk.listings.show(params);
  return show
    .then(res => {
      const denormalisedListing = denormalisedResponseEntities(res)[0];
      dispatch(showListingSuccess(denormalisedListing));
      return denormalisedListing;
    })
    .catch(e => {
      dispatch(showListingError(storableError(e)));
    });
};

// Thunk: loadData for SSR or route
export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const { blogId, listingId } = params;
  const blogPromise = blogId ? dispatch(requestFetchBlog(blogId)) : Promise.resolve();
  const listingPromise = listingId ? dispatch(requestShowListing(listingId, config)) : Promise.resolve();
  return Promise.all([blogPromise, listingPromise])
  .then(() => {
    dispatch(incrementBlogViewsAndClicks(blogId, true));
  });
};